import { useTranslation } from "react-i18next";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";

// Vista read-only con los datos generales disponibles sobre el colaborador.
// Reutiliza la misma información que se muestra en la modal/drawer (InvolvedInfo),
// pero recibe el involucrado por prop para poder mostrarse en un colapsable.
export const CollaboratorDetail = ({ involved }) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();

  if (!involved) return null;

  const hasBasicInfo = involved?.email || involved?.username || involved?.userId || involved?.additional || involved?.employeeType;
  const hasOrganization = involved?.division || involved?.department || involved?.businessUnit || involved?.company || involved?.area;
  const hasLocation = involved?.country || involved?.province || involved?.locationName;
  const hasPosition = involved?.position || involved?.relationship || involved?.jobCode || involved?.costCenter;
  const hasDates = involved?.hireDate || involved?.positionEntryDate || involved?.meliEntryDate;
  const hasIdentifications = involved?.primaryNationalId?.nationalId || involved?.rfcTaxId || involved?.nationalIds?.length > 0;
  const hasRelationships = involved?.manager?.userId || involved?.pbp?.relUserId || involved?.pbpLeaderInfo?.userId || involved?.taOwner?.relUserId || involved?.relationships?.pbp || involved?.relationships?.pbpLeader || involved?.relationships?.taOwner;

  const hasAnyData = hasBasicInfo || hasOrganization || hasLocation || hasPosition || hasDates || hasIdentifications || hasRelationships;

  if (!hasAnyData) {
    return (
      <Typography fontSize={12} color="secondary" fontStyle="italic">
        {t("involved.no_collaborator_data")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        columnGap: 2,
        columnCount: { xs: 1, sm: 1, md: 3 },
      }}
    >

      {/* ═══════════════════════════════════════════════════════════════
          SECCIÓN: Información Básica
      ═══════════════════════════════════════════════════════════════ */}
      {hasBasicInfo && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "12px 16px",
            border: `1px solid ${theme.palette.blueGray200.main}`,
            breakInside: "avoid",
            marginBottom: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5} pb={1} sx={{ borderBottom: `1px solid ${theme.palette.blueGray200.main}`, mx: "-16px", px: "16px" }}>
            <Typography fontSize={13} fontWeight={700} color="oxfordBlue.main">
              {t("involved.section_basic_info")}
            </Typography>
          </Stack>
          <Grid2 container spacing={1.5}>
            {involved?.userId && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.user_id")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.userId}</Typography>
              </Grid2>
            )}
            {involved?.username && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.username")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.username}</Typography>
              </Grid2>
            )}
            {involved?.additional && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.employee_number")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.additional}</Typography>
              </Grid2>
            )}
            {involved?.employeeType && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.employee_type")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.employeeType === "internal" ? t("involved.internal") : t("involved.external")}</Typography>
              </Grid2>
            )}
            {involved?.email && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.email")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.email}</Typography>
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SECCIÓN: Organización
      ═══════════════════════════════════════════════════════════════ */}
      {hasOrganization && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "12px 16px",
            border: `1px solid ${theme.palette.blueGray200.main}`,
            breakInside: "avoid",
            marginBottom: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5} pb={1} sx={{ borderBottom: `1px solid ${theme.palette.blueGray200.main}`, mx: "-16px", px: "16px" }}>
            <Typography fontSize={13} fontWeight={700} color="oxfordBlue.main">
              {t("involved.section_organization")}
            </Typography>
          </Stack>
          <Grid2 container spacing={1.5}>
            {involved?.division && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.division")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.division}</Typography>
              </Grid2>
            )}
            {involved?.department && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.department")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.department}</Typography>
              </Grid2>
            )}
            {involved?.businessUnit && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.business_unit")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.businessUnit}</Typography>
              </Grid2>
            )}
            {involved?.company && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.company")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.company}</Typography>
              </Grid2>
            )}
            {involved?.area && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.area")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.area}</Typography>
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SECCIÓN: Ubicación
      ═══════════════════════════════════════════════════════════════ */}
      {hasLocation && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "12px 16px",
            border: `1px solid ${theme.palette.blueGray200.main}`,
            breakInside: "avoid",
            marginBottom: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5} pb={1} sx={{ borderBottom: `1px solid ${theme.palette.blueGray200.main}`, mx: "-16px", px: "16px" }}>
            <Typography fontSize={13} fontWeight={700} color="oxfordBlue.main">
              {t("involved.section_location")}
            </Typography>
          </Stack>
          <Grid2 container spacing={1.5}>
            {involved?.country && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.country")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.country}</Typography>
              </Grid2>
            )}
            {involved?.province && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.province")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.province}</Typography>
              </Grid2>
            )}
            {involved?.locationName && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.location")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.locationName}</Typography>
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SECCIÓN: Puesto
      ═══════════════════════════════════════════════════════════════ */}
      {hasPosition && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "12px 16px",
            border: `1px solid ${theme.palette.blueGray200.main}`,
            breakInside: "avoid",
            marginBottom: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5} pb={1} sx={{ borderBottom: `1px solid ${theme.palette.blueGray200.main}`, mx: "-16px", px: "16px" }}>
            <Typography fontSize={13} fontWeight={700} color="oxfordBlue.main">
              {t("involved.section_position")}
            </Typography>
          </Stack>
          <Grid2 container spacing={1.5}>
            {(involved?.position || involved?.relationship) && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.cargo")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.position || involved.relationship}</Typography>
              </Grid2>
            )}
            {involved?.jobCode && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.job_code")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.jobCode}</Typography>
              </Grid2>
            )}
            {involved?.costCenter && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.cost_center")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.costCenter}</Typography>
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SECCIÓN: Fechas
      ═══════════════════════════════════════════════════════════════ */}
      {hasDates && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "12px 16px",
            border: `1px solid ${theme.palette.blueGray200.main}`,
            breakInside: "avoid",
            marginBottom: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5} pb={1} sx={{ borderBottom: `1px solid ${theme.palette.blueGray200.main}`, mx: "-16px", px: "16px" }}>
            <Typography fontSize={13} fontWeight={700} color="oxfordBlue.main">
              {t("involved.section_dates")}
            </Typography>
          </Stack>
          <Grid2 container spacing={1.5}>
            {involved?.hireDate && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.hire_date")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.hireDate}</Typography>
              </Grid2>
            )}
            {involved?.positionEntryDate && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.position_entry_date")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.positionEntryDate}</Typography>
              </Grid2>
            )}
            {involved?.meliEntryDate && (
              <Grid2 size={{ xs: 6 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.meli_entry_date")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.meliEntryDate}</Typography>
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SECCIÓN: Identificaciones
      ═══════════════════════════════════════════════════════════════ */}
      {hasIdentifications && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "12px 16px",
            border: `1px solid ${theme.palette.blueGray200.main}`,
            breakInside: "avoid",
            marginBottom: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5} pb={1} sx={{ borderBottom: `1px solid ${theme.palette.blueGray200.main}`, mx: "-16px", px: "16px" }}>
            <Typography fontSize={13} fontWeight={700} color="oxfordBlue.main">
              {t("involved.section_identifications")}
            </Typography>
          </Stack>
          <Grid2 container spacing={1.5}>
            {involved?.primaryNationalId?.nationalId && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.national_id")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">
                  {`${involved.primaryNationalId.country || ""} - ${involved.primaryNationalId.nationalId}`}
                </Typography>
              </Grid2>
            )}
            {involved?.rfcTaxId && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.rfc_tax_id")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">{involved.rfcTaxId}</Typography>
              </Grid2>
            )}
            {involved?.nationalIds?.length > 1 && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.other_national_ids")}</Typography>
                {involved.nationalIds
                  .filter(id => id.nationalId !== involved?.primaryNationalId?.nationalId)
                  .map((id, index) => (
                    <Typography key={index} fontSize={12} color="oxfordBlue.main">
                      {`${id.country || ""} (${id.cardType || ""}) - ${id.nationalId || ""}`}
                    </Typography>
                  ))
                }
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SECCIÓN: Relaciones Laborales
      ═══════════════════════════════════════════════════════════════ */}
      {hasRelationships && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "12px 16px",
            border: `1px solid ${theme.palette.blueGray200.main}`,
            breakInside: "avoid",
            marginBottom: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5} pb={1} sx={{ borderBottom: `1px solid ${theme.palette.blueGray200.main}`, mx: "-16px", px: "16px" }}>
            <Typography fontSize={13} fontWeight={700} color="oxfordBlue.main">
              {t("involved.section_relationships")}
            </Typography>
          </Stack>
          <Grid2 container spacing={1.5}>
            {involved?.manager?.userId && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.manager")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">
                  {`${involved.manager.firstName || ""} ${involved.manager.lastName || ""}`.trim() || involved.manager.email || involved.manager.userId}
                </Typography>
                {involved.manager.email && (
                  <Typography fontSize={11} color="secondary">{involved.manager.email}</Typography>
                )}
              </Grid2>
            )}
            {/* PBP Leader - desde pbpLeaderInfo o relationships.pbpLeader */}
            {(involved?.pbpLeaderInfo?.userId || involved?.relationships?.pbpLeader) && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.pbp_leader")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">
                  {involved?.pbpLeaderInfo?.userId
                    ? `${involved.pbpLeaderInfo.firstName || ""} ${involved.pbpLeaderInfo.lastName || ""}`.trim() || involved.pbpLeaderInfo.userId
                    : `${involved.relationships.pbpLeader.firstName || ""} ${involved.relationships.pbpLeader.lastName || ""}`.trim() || involved.relationships.pbpLeader.relUserId
                  }
                </Typography>
                <Typography fontSize={11} color="secondary">
                  {involved?.pbpLeaderInfo?.email || involved?.relationships?.pbpLeader?.email || ""}
                </Typography>
              </Grid2>
            )}
            {/* PBP - desde pbp o relationships.pbp */}
            {(involved?.pbp?.relUserId || involved?.relationships?.pbp) && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.pbp")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">
                  {involved?.relationships?.pbp
                    ? `${involved.relationships.pbp.firstName || ""} ${involved.relationships.pbp.lastName || ""}`.trim() || involved.relationships.pbp.relUserId
                    : involved.pbp.relUserId
                  }
                </Typography>
                {(involved?.relationships?.pbp?.email || involved?.pbp?.email) && (
                  <Typography fontSize={11} color="secondary">
                    {involved?.relationships?.pbp?.email || involved?.pbp?.email}
                  </Typography>
                )}
              </Grid2>
            )}
            {/* TA Owner - desde taOwner o relationships.taOwner */}
            {(involved?.taOwner?.relUserId || involved?.relationships?.taOwner) && (
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={700} color="secondary">{t("involved.ta_owner")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">
                  {involved?.relationships?.taOwner
                    ? `${involved.relationships.taOwner.firstName || ""} ${involved.relationships.taOwner.lastName || ""}`.trim() || involved.relationships.taOwner.relUserId
                    : involved.taOwner.relUserId
                  }
                </Typography>
                {(involved?.relationships?.taOwner?.email || involved?.taOwner?.email) && (
                  <Typography fontSize={11} color="secondary">
                    {involved?.relationships?.taOwner?.email || involved?.taOwner?.email}
                  </Typography>
                )}
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}

    </Box>
  );
};
